<script lang="ts">
  import Dialog from './Dialog.svelte';
  import Icon from './Icon.svelte';
  import LocationBlockedHelp from './LocationBlockedHelp.svelte';
  import { photoUrl } from './api';
  import { formatDistanceFrom } from '~/shared/group-actions.js';
  import { formatTypeLabel } from '~/shared/place-type.js';
  import { routeActionLabel } from '~/shared/route-handoff.js';
  import type { PoiView } from '~/shared/api.js';

  let {
    poi,
    contextLabel,
    contextIcon = 'shuffle',
    distanceM,
    originLabel,
    locationBlocked = false,
    canPickAnother = false,
    routeEmpty = true,
    inRoute = false,
    onClose,
    onAnother,
    onAddToRoute,
  }: {
    poi: PoiView;
    contextLabel: string;
    contextIcon?: 'shuffle' | 'map-pin' | 'sparkles';
    distanceM?: number;
    /** When set, distance reads "X km from {originLabel}" instead of "X km away".
     *  Use it to make the source explicit (e.g. "your location" vs the active city). */
    originLabel?: string;
    /** True when geolocation denial forced a fallback origin — surfaces a
     *  collapsible "Why isn't my location used?" inline help. */
    locationBlocked?: boolean;
    canPickAnother?: boolean;
    routeEmpty?: boolean;
    inRoute?: boolean;
    onClose: () => void;
    onAnother?: () => void;
    /** Add this surprised POI to the route + open the Route view. */
    onAddToRoute?: (poi: PoiView) => void;
  } = $props();

  const displayName = $derived(poi.customName ?? poi.name);
  const src = $derived(photoUrl(poi.photoReference, 1080));
  const routeButtonLabel = $derived(routeActionLabel({ inRoute, routeEmpty }));

</script>

<Dialog {onClose} variant="sheet">
  <div class="reveal" role="dialog" aria-modal="true" aria-label="Group action result">
    <div class="photo-wrap">
      {#if src}
        <img src={src} alt="" />
      {:else}
        <div class="no-photo"></div>
      {/if}
      <span class="context-badge">
        <Icon name={contextIcon} size={12} strokeWidth={2.25} />
        <span>{contextLabel}</span>
      </span>
      <button type="button" class="close-btn" aria-label="Close" onclick={onClose}>
        <Icon name="x" size={16} />
      </button>
    </div>

    <div class="body">
      <div class="title">
        <h2>{displayName}</h2>
        <div class="meta">
          {#if poi.rating !== null}
            <span class="rating">
              <Icon name="star" size={13} />
              <span class="tabular-nums">{poi.rating.toFixed(1)}</span>
            </span>
            <span aria-hidden="true">·</span>
          {/if}
          {#if poi.category}
            <span>{formatTypeLabel(poi.category)}</span>
          {/if}
          {#if distanceM !== undefined}
            <span aria-hidden="true">·</span>
            <span>{formatDistanceFrom(distanceM, originLabel)}</span>
          {/if}
        </div>
      </div>

      {#if poi.notes}
        <p class="notes">{poi.notes}</p>
      {/if}

      {#if locationBlocked}
        <LocationBlockedHelp compact />
      {/if}

      <div class="actions">
        {#if canPickAnother && onAnother}
          <button class="btn btn-tertiary pick-another" type="button" onclick={onAnother}>
            <Icon name="shuffle" size={14} strokeWidth={2} />
            <span>Pick another</span>
          </button>
        {/if}
        {#if onAddToRoute}
          <button
            class="btn btn-primary route-btn"
            type="button"
            disabled={inRoute}
            onclick={() => {
              onAddToRoute(poi);
              onClose();
            }}
          >
            <span>{routeButtonLabel}</span>
          </button>
        {/if}
      </div>
    </div>
  </div>
</Dialog>

<style>
  .reveal {
    position: relative;
    background: var(--color-canvas);
    border-radius: var(--radius-md) var(--radius-md) 0 0;
    width: 100%;
    max-width: 580px;
    box-shadow:
      rgba(0, 0, 0, 0.02) 0 0 0 1px,
      rgba(0, 0, 0, 0.04) 0 2px 6px 0,
      rgba(0, 0, 0, 0.10) 0 4px 8px 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
  @media (min-width: 768px) {
    .reveal { border-radius: var(--radius-md); }
  }
  .photo-wrap {
    position: relative;
    aspect-ratio: 16 / 10;
    background: var(--color-surface-strong);
    overflow: hidden;
  }
  .photo-wrap img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .no-photo { width: 100%; height: 100%; }
  .context-badge {
    position: absolute;
    top: 0.75rem;
    left: 0.75rem;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(31, 29, 26, 0.8);
    color: var(--color-on-dark);
    border-radius: 9999px;
    padding: 5px 10px;
    font-size: 11px;
    font-weight: 600;
    backdrop-filter: blur(4px);
  }
  .close-btn {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    background: var(--color-canvas);
    color: var(--color-ink);
    border: 0;
    width: 36px;
    height: 36px;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: rgba(0, 0, 0, 0.16) 0 4px 8px 0;
  }
  .body {
    padding: 1.25rem 1.5rem 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }
  .title { display: flex; flex-direction: column; gap: 0.375rem; }
  .title h2 {
    margin: 0;
    font-size: 24px;
    font-weight: 600;
    color: var(--color-ink);
    line-height: 1.2;
  }
  .meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.375rem;
    font-size: 14px;
    color: var(--color-muted);
  }
  .rating {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--color-ink);
  }
  .rating .tabular-nums { font-weight: 500; }
  .notes {
    margin: 0;
    font-size: 15px;
    color: var(--color-charcoal);
  }
  .actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .route-btn { margin-left: auto; }
  @media (max-width: 640px) {
    .actions {
      flex-direction: column;
      align-items: stretch;
    }
    .actions .btn { width: 100%; justify-content: center; }
    .route-btn { margin-left: 0; order: 1; }
    .pick-another { order: 2; }
  }
</style>
